import {useSelector} from 'react-redux'

import {selectUser} from 'features/User'
import {DOPE_PRIMARY_DOMAIN} from 'definitions/constant'
import {EnvironmentEnum} from 'interfaces'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'
import {selectSSLInspectionDisabledInSnapshot} from 'features/Policies/policiesSelectors'
import {useSSLInspectionRelease} from './useSSLInspectionRelease'

interface useFeatureFlagReturnType {
  isDopeTenant: boolean
  isPaywalled: boolean
  developmentOnlyFeatureFlag: boolean
  stagingOnlyFeatureFlag: boolean
  nonProductionFeatureFlag: boolean
  dopeInternalFeatureFlag: boolean
  productionDopeFeatureFlag: boolean
  internalAndDopeProductionFeatureFlag: boolean
  isDLPComingSoonScreenEnabled: boolean
  swgDlpBlock: boolean
  swgDlpEarlyAccess: boolean
  casbExportEnabled: boolean
  isSSPMTableViewEnabled: boolean
  isSSLInspectionToggleFeatureFlag: boolean
  isSSLInspectionDisabled: boolean
}

/**
 * @description This hook is used to get the feature flag values
 */
export const useFeatureFlag = (): useFeatureFlagReturnType => {
  const user = useSelector(selectUser)
  const {isSSLInspectionToggleReleased} = useSSLInspectionRelease()

  const isSSLInspectionDisabled = useSelector(
    selectSSLInspectionDisabledInSnapshot,
  )
  const {
    casbFullAccess,
    swgDlpBlock,
    swgDlpEarlyAccess,
    sspmEarlyAccess,
    isLoading,
  } = useEntitlementsQuery({
    enabled: false,
  })
  const isPaywalled = !casbFullAccess && !isLoading

  const primaryDomain = user?.primaryDomain

  /**
   * Default global feature flags for the application.
   */
  const isDopeTenant = primaryDomain === DOPE_PRIMARY_DOMAIN

  const developmentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEVELOPMENT

  const stagingOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.STAGING

  const nonProductionFeatureFlag =
    process.env.REACT_APP_ENV !== EnvironmentEnum.PRODUCTION

  // true if the environment is not production and primaryDomain is dope.security
  const dopeInternalFeatureFlag = nonProductionFeatureFlag && isDopeTenant

  // true if the environment is production and primaryDomain is dope.security
  const productionDopeFeatureFlag = !nonProductionFeatureFlag && isDopeTenant

  // true if the environment is not production or if it's production and primaryDomain is dope.security
  const internalAndDopeProductionFeatureFlag =
    nonProductionFeatureFlag || productionDopeFeatureFlag

  // DLP coming soon screen - enabled for users who do not have early access
  const isDLPComingSoonScreenEnabled = !swgDlpEarlyAccess

  // CASB export feature - enabled for non-production environments
  const isTestEnvironment = process.env.NODE_ENV === 'test'
  const casbExportEnabled = nonProductionFeatureFlag || isTestEnvironment

  // SSL Inspection toggle feature flag
  const isSSLInspectionToggleFeatureFlag =
    nonProductionFeatureFlag || isSSLInspectionToggleReleased

  /**
   * Individual feature flags for different features on the platform.
   */
  const isSSPMTableViewEnabled: boolean = sspmEarlyAccess

  return {
    nonProductionFeatureFlag,
    isDopeTenant,
    developmentOnlyFeatureFlag,
    stagingOnlyFeatureFlag,
    dopeInternalFeatureFlag,
    productionDopeFeatureFlag,
    internalAndDopeProductionFeatureFlag,
    isPaywalled,
    isDLPComingSoonScreenEnabled,
    swgDlpBlock,
    swgDlpEarlyAccess,
    casbExportEnabled,
    isSSPMTableViewEnabled,
    isSSLInspectionToggleFeatureFlag,
    isSSLInspectionDisabled:
      isSSLInspectionToggleFeatureFlag && isSSLInspectionDisabled,
  }
}
